import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { fetchWithCache } from '../services/cache';
import LazyImage from '../components/LazyImage';
import HeroSlider from '../components/HeroSlider';
import { Button } from '../components/ui/Button';
import { DonateButton } from '../components/Header';
import { FaRegHeart } from 'react-icons/fa';
import { RotatingLines } from 'react-loader-spinner';
import { INITIAL_HOME_DATA } from '../utils/initialData';
import { getImageUrl } from '../utils/imageUtils';

const Section = styled.section`
  padding: 4rem 2rem;
  background-color: ${({ $bg, theme }) => $bg || theme.colors.white};
`;

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.large};
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 2.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
`;

const TextContent = styled.div`
  p {
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageContainer = styled.div`
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
`;

const SnippetCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.small};
  text-align: center;
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const Home = () => {
  const [data, setData] = useState(INITIAL_HOME_DATA);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchWithCache('homeData', () => api.get('/home'), setData);
      } catch (err) {
        console.error(err);
      }
    };
    loadData();
  }, []);

  if (!data) return <div style={{padding: '2rem'}}>Failed to load content</div>;

  const { hero, snippets } = data;

  return (
    <div>
      <HeroSlider images={hero.images} text={hero.text} />

      {/* About Snippet */}
      <Section>
        <Container>
          <Grid>
            <ImageContainer>
               {snippets.about.image && <img src={getImageUrl(snippets.about.image)} alt="About Us" style={{width:'100%', height:'100%', objectFit:'contain'}} />}
            </ImageContainer>
            <TextContent>
              <SectionTitle>{snippets.about.title}</SectionTitle>
              <p>{snippets.about.content}</p>
              <Link to="/about">
                <Button $variant="secondary">Read More</Button>
              </Link>
            </TextContent>
          </Grid>
        </Container>
      </Section>

      {/* Workshops & Outreach Snippets */}
      <Section>
        <Container>
          <Grid>
             <SnippetCard>
                {snippets.workshop.image && (
                    <div style={{height: '200px', marginBottom: '1rem', overflow:'hidden', borderRadius:'4px', display:'flex', alignItems:'center', justifyContent:'center', background:'#f0f0f0'}}>
                        <img src={getImageUrl(snippets.workshop.image)} alt="Workshops" style={{width:'100%', height:'100%', objectFit:'contain'}} />
                    </div>
                )}
                <h3>{snippets.workshop.title}</h3>
                <p>{snippets.workshop.content}</p>
                <Link to="/workshops">
                    <Button $variant="tertiary" style={{marginTop: '1rem'}}>Learn More</Button>
                </Link>
             </SnippetCard>
             <SnippetCard>
                {snippets.outreach.image && (
                    <div style={{height: '200px', marginBottom: '1rem', overflow:'hidden', borderRadius:'4px', display:'flex', alignItems:'center', justifyContent:'center', background:'#f0f0f0'}}>
                        <img src={getImageUrl(snippets.outreach.image)} alt="Outreach" style={{width:'100%', height:'100%', objectFit:'contain'}} />
                    </div>
                )}
                <h3>{snippets.outreach.title}</h3>
                <p>{snippets.outreach.content}</p>
                <Link to="/outreach">
                    <Button $variant="tertiary" style={{marginTop: '1rem'}}>Learn More</Button>
                </Link>
             </SnippetCard>
          </Grid>
        </Container>
      </Section>
      
      {/* Donate Snippet */}
      <Section $bg="#e8f4fd">
         <Container>
            <Grid>
                <TextContent>
                    <SectionTitle>{snippets.donate.title}</SectionTitle>
                    <p>{snippets.donate.content}</p>
                    <DonateButton to="/donate">
                        Give <FaRegHeart size={15} />
                    </DonateButton>
                </TextContent>
                 <ImageContainer>
                   {snippets.donate.image && <img src={getImageUrl(snippets.donate.image)} alt="Donate" style={{width:'100%', height:'100%', objectFit:'contain'}} />}
                </ImageContainer>
            </Grid>
         </Container>
      </Section>

      {/* Contact Snippet */}
      <Section>
        <Container>
            <SectionTitle>{snippets.contact.title}</SectionTitle>
            <p style={{textAlign:'center', marginBottom: '2rem'}}>{snippets.contact.content}</p>
            <div style={{textAlign:'center'}}>
                 <Link to="/contact">
                    <Button>Contact Us</Button>
                </Link>
            </div>
        </Container>
      </Section>
    </div>
  );
};

export default Home;
